import { useState } from "react";
import { Outlet, useParams } from "react-router";
import { useAuth } from "../context/auth";

export function RequireVerified() {
	const { user, signOut } = useAuth();
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const [sending, setSending] = useState(false);
	const [sent, setSent] = useState(false);
	const [error, setError] = useState("");

	if (user?.emailVerified) return <Outlet />;

	async function handleResend() {
		setError("");
		setSending(true);
		setSent(false);
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/auth/send-verification-email`,
				{
					method: "POST",
					credentials: "include",
					headers: {
						"Content-Type": "application/json",
						Origin: window.location.origin,
					},
					body: JSON.stringify({
						email: user?.email,
						callbackURL: window.location.href,
					}),
				},
			);
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setError(
					(body as { message?: string } | null)?.message ??
						`Failed to resend (${res.status})`,
				);
				return;
			}
			setSent(true);
		} catch {
			setError("Network error");
		} finally {
			setSending(false);
		}
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50">
			<div className="max-w-sm w-full p-6 bg-white rounded-lg shadow text-center">
				<h1 className="text-2xl font-bold text-gray-900 mb-2">
					Check your email
				</h1>
				<p className="text-sm text-gray-600 mb-6">
					We sent a verification link to{" "}
					<span className="font-medium text-gray-900">{user?.email}</span>.
					Click the link to verify your account.
				</p>
				{error && <p className="text-red-600 text-sm mb-3">{error}</p>}
				{sent && (
					<p className="text-green-600 text-sm mb-3">
						Verification email sent!
					</p>
				)}
				<button
					type="button"
					onClick={handleResend}
					disabled={sending}
					className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 mb-3"
				>
					{sending ? "Sending\u2026" : "Resend verification email"}
				</button>
				<button
					type="button"
					onClick={signOut}
					className="w-full rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
				>
					Sign out
				</button>
			</div>
		</div>
	);
}
